import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { fitCamera } from '../geometry';
import { generateEdges } from '../../FlowchartView/utils/edgePaths';
import { calculateLayoutElk } from '../../FlowchartView/layout/elkLayout';
import { useNodePositions } from '../../FlowchartView/hooks/useNodePositions';
import { useEdgePathCache } from '../../FlowchartView/hooks/useEdgePathCache';
import { sizeOf } from '../utils/nodeSize';
import { place, setBoardCameraRecorder } from '../utils/placeCamera';
import { BOARD_FRAME } from '../events';
import { recallBoardCamera, rememberBoardCamera } from '../utils/boardCameraMemory';
import { buildGraph, buildLayoutKey } from '../utils/boardScene';
import { playFokusReturn, persistablePositionChanges, slideBoard } from '../utils/slideBoard';

const NO_CUSTOM_POSITIONS = {};

/**
 * Card positions, measured sizes, and the wires between them.
 * The camera is fitted only when a campaign opens, then once when layout arrives.
 */
export function useBoardScene({
  tasks,
  campaignId,
  tabId,
  wrapRef,
  worldRef,
  cameraRef,
  flyFrameRef,
  gestureRef,
  onPositionChangeBatchRef,
  tidyFocusRef,
  tidySettledRef,
  frameAfterLayoutRef,
  fokusActive = false,
  chainOrder = null,
}) {
  const tasksRef = useRef(tasks);
  const positionsRef = useRef(null);
  const boxesRef = useRef({});
  const fitStampRef = useRef('');
  const memoryKeyRef = useRef(null);
  const laidOutRef = useRef(false);
  const slideCancelRef = useRef(null);
  const prevFokusRef = useRef(false);
  const fullLayoutRef = useRef(null);
  const restoreLayoutRef = useRef(false);
  const restoreFromRef = useRef(null);
  const returnPlayedRef = useRef(false);
  const fokusLiveRef = useRef(false);
  const chainOrderRef = useRef(chainOrder);
  chainOrderRef.current = chainOrder;
  const chainOrderKey = chainOrder && chainOrder.length ? chainOrder.join(',') : '';
  const [boxes, setBoxes] = useState({});
  const [elk, setElk] = useState(null);
  tasksRef.current = tasks;
  boxesRef.current = boxes;
  fokusLiveRef.current = !!fokusActive;

  // Remember the whole board when FOKUS starts, and put that picture back
  // the moment it ends. Leaving must not lay every mission out again.
  if (fokusActive && !prevFokusRef.current && elk?.positions) {
    fullLayoutRef.current = {
      result: elk,
      camera: cameraRef.current ? { ...cameraRef.current } : null,
    };
  }
  const restoring = !fokusActive && prevFokusRef.current && !!fullLayoutRef.current?.result;
  if (restoring) {
    restoreLayoutRef.current = true;
    restoreFromRef.current = positionsRef.current;
    returnPlayedRef.current = false;
  }
  prevFokusRef.current = !!fokusActive;

  const sceneKey = `${tabId || ''}:${campaignId || ''}`;
  const sceneKeyRef = useRef(sceneKey);
  if (sceneKeyRef.current !== sceneKey) {
    sceneKeyRef.current = sceneKey;
    laidOutRef.current = false;
    if (elk) setElk(null);
  }

  const layoutSource = restoring ? fullLayoutRef.current.result : elk;
  const elkPositions = layoutSource?.positions || NO_CUSTOM_POSITIONS;
  const elkCoversTasks = tasks.length > 0 && tasks.every((task) => (
    !task?.id || Number.isFinite(elkPositions[task.id]?.x)
  ));
  const positionRecord = useNodePositions({
    tasks,
    autoPositions: elkPositions,
    customPositions: NO_CUSTOM_POSITIONS,
    isMultiCampaignMode: false,
    useAutoPositions: elkCoversTasks,
    hasActiveFilters: false,
    elkPositionsOk: Object.keys(elkPositions).length > 0,
  });
  const positions = useMemo(() => {
    const map = new Map();
    Object.entries(positionRecord).forEach(([id, pos]) => {
      if (pos) map.set(id, pos);
    });
    return map;
  }, [positionRecord]);
  positionsRef.current = positions;

  const graph = useMemo(
    () => buildGraph(tasks, positionRecord, boxes),
    [tasks, positionRecord, boxes],
  );
  const edgesRaw = useMemo(
    () => generateEdges(tasks, graph.nodePositions, graph.nodeDimensions),
    [tasks, graph],
  );
  const edges = useEdgePathCache(edgesRaw, layoutSource?.edgePaths || null, {
    nodePositions: graph.nodePositions,
    autoPositions: elkPositions,
  });
  const layoutKey = useMemo(() => buildLayoutKey(tasks, boxes), [tasks, boxes]);

  useEffect(() => {
    laidOutRef.current = false;
  }, [campaignId, tabId]);

  useLayoutEffect(() => {
    if (!restoreLayoutRef.current) return undefined;
    const saved = fullLayoutRef.current;
    if (!saved?.result) return undefined;
    slideCancelRef.current?.();
    slideCancelRef.current = null;
    if (flyFrameRef.current) {
      cancelAnimationFrame(flyFrameRef.current);
      flyFrameRef.current = 0;
    }
    // The picture is already the saved board. One more render lets React
    // put every card on its home spot before the slide and the fade start.
    if (elk !== saved.result) {
      setElk(saved.result);
      return undefined;
    }
    if (returnPlayedRef.current) return undefined;
    returnPlayedRef.current = true;
    laidOutRef.current = true;
    const cancel = playFokusReturn({
      world: worldRef.current,
      wrap: wrapRef.current,
      from: restoreFromRef.current,
      to: saved.result.positions,
      cameraRef,
      cameraTo: saved.camera,
    });
    return () => {
      cancel();
      returnPlayedRef.current = false;
    };
  }, [tasks, fokusActive, elk, worldRef, wrapRef, cameraRef, flyFrameRef]);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const run = () => {
      if (cancelled) return;
      if (restoreLayoutRef.current) {
        restoreLayoutRef.current = false;
        if (frameAfterLayoutRef) frameAfterLayoutRef.current = false;
        laidOutRef.current = true;
        return;
      }
      // Layout of the whole board is heavy. Wait out a glide or a hand drag.
      if (flyFrameRef.current || gestureRef.current) {
        timer = window.setTimeout(run, 40);
        return;
      }
      const list = tasksRef.current || [];
      const dims = {};
      list.forEach((task) => {
        if (!task?.id) return;
        const size = sizeOf(task, boxesRef.current);
        dims[task.id] = { width: Math.round(size.width), height: Math.round(size.height) };
      });
      calculateLayoutElk(list, dims, chainOrderRef.current).then((result) => {
        const publish = () => {
          if (cancelled || !result) return;
          if (flyFrameRef.current || gestureRef.current) {
            timer = window.setTimeout(publish, 40);
            return;
          }
          const commit = (save) => {
            if (cancelled) return;
            const saveChanges = save && !fokusLiveRef.current
              ? persistablePositionChanges(tasksRef.current, result.positions)
              : [];
            flushSync(() => {
              setElk(result);
              if (saveChanges.length) {
                onPositionChangeBatchRef?.current?.(saveChanges, {
                  batchType: 'TIDY',
                  batchDescription: 'Tidy',
                  skipRecordAction: true,
                });
              }
            });
            if (save) tidySettledRef?.current?.();
          };
          // Opening a campaign used to keep the saved coordinates, so a gappy
          // top row stayed on screen. Slide onto the fresh layout and save it.
          laidOutRef.current = true;
          const focusId = tidyFocusRef?.current || null;
          if (tidyFocusRef) tidyFocusRef.current = null;
          if (frameAfterLayoutRef?.current) {
            frameAfterLayoutRef.current = false;
            window.dispatchEvent(new CustomEvent(BOARD_FRAME, {
              detail: { tasks: list, positions: result.positions },
            }));
          }
          slideCancelRef.current?.();
          slideCancelRef.current = slideBoard({
            world: worldRef.current,
            wrap: wrapRef.current,
            from: positionsRef.current,
            to: result.positions,
            cameraRef,
            focusId,
            tasks: tasksRef.current,
            boxes: boxesRef.current,
            onDone: () => commit(true),
          });
        };
        publish();
      }).catch(() => {});
    };
    timer = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      slideCancelRef.current?.();
      slideCancelRef.current = null;
    };
    // layoutKey already folds in task positions, measured sizes, and connections.
    // chainOrderKey re-packs the top row after a horizontal reorder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, chainOrderKey, flyFrameRef, gestureRef]);

  useLayoutEffect(() => {
    setBoardCameraRecorder((camera) => {
      const key = memoryKeyRef.current;
      if (!key) return;
      rememberBoardCamera(key.tabId, key.campaignId, camera);
    });
    return () => {
      const key = memoryKeyRef.current;
      if (key) rememberBoardCamera(key.tabId, key.campaignId, cameraRef.current);
      setBoardCameraRecorder(null);
    };
  }, [cameraRef]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const id = campaignId || '';
    const stamp = `${tabId || ''}:${id}`;
    const campaignChanged = !fitStampRef.current.startsWith(`${stamp}:`);
    const elkReady = Boolean(elk);
    if (campaignChanged) {
      const prevKey = memoryKeyRef.current;
      if (prevKey) rememberBoardCamera(prevKey.tabId, prevKey.campaignId, cameraRef.current);
      memoryKeyRef.current = { tabId: tabId || null, campaignId: id };
      const saved = recallBoardCamera(tabId, id);
      if (saved && !flyFrameRef.current) {
        cameraRef.current = { ...saved };
        fitStampRef.current = `${stamp}:laid-out`;
      } else {
        fitStampRef.current = `${stamp}:waiting`;
        if (!flyFrameRef.current) {
          cameraRef.current = fitCamera(tasks, positions, wrap.clientWidth, wrap.clientHeight);
        }
      }
    } else if (elkReady && fitStampRef.current === `${stamp}:waiting`) {
      fitStampRef.current = `${stamp}:laid-out`;
      if (!flyFrameRef.current) {
        cameraRef.current = fitCamera(tasks, positions, wrap.clientWidth, wrap.clientHeight);
      }
    }
    if (!flyFrameRef.current) place(worldRef.current, cameraRef.current);
  }, [tasks, positions, elk, campaignId, tabId, wrapRef, worldRef, cameraRef, flyFrameRef]);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const measure = () => {
      if (cancelled) return;
      if (flyFrameRef.current || gestureRef.current) {
        timer = window.setTimeout(measure, 40);
        return;
      }
      const wrap = wrapRef.current;
      if (!wrap) return;
      const prev = boxesRef.current;
      const next = { ...prev };
      let changed = false;
      wrap.querySelectorAll('[data-board-node]').forEach((el) => {
        const nodeId = el.getAttribute('data-board-node');
        if (!nodeId || prev[nodeId]) return;
        const card = el.querySelector('.task-node, .decision-node, .note-node, .milestone-node') || el;
        next[nodeId] = { w: card.offsetWidth, h: card.offsetHeight };
        changed = true;
      });
      if (changed) setBoxes(next);
    };
    timer = window.setTimeout(measure, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [tasks, wrapRef, flyFrameRef, gestureRef]);

  return { tasksRef, positions, positionsRef, boxesRef, graph, edges };
}
